import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Box, styled } from '@mui/system';
import { Alert, Breadcrumbs, Link, Snackbar, Typography } from '@mui/material';
import { colors } from '../styles/globalStyles';
import { ReactComponent as DotDivider } from '../images/dot_divider.svg';
import PatternedButton from '../components/inputs/patternedButton';
import SessionCreateModal from '../components/pages/adminPanel/sessionCreateModal';
import { getExcel, getTeamInfo, updateStep } from '../api/adminPanel';
import InstructionsModal from '../components/modals/instructionsModal';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { getUserData } from '../api/auth';
import { userAtom } from '../recoil/atoms/userAtom';
import { selectedLanguageAtom } from '../recoil/atoms/languageAtom';
import textData from '../data/data.json';

const StyledBreadcrumbs = styled(Breadcrumbs)({
    '& a.light': {
        opacity: 0.4,
        color: colors.darkBlue,
    },

    '& a.transparent': {
        opacity: 0.4,
        color: colors.white,
    },

    '& a.current.light': {
        opacity: 1,
        color: colors.main,
    },

    '& a.current.transparent': {
        opacity: 1,
        color: colors.white,
    },
});

const ButtonsWrap = styled('div')(({ gap = 17 }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: gap,
}));

const StepWrapper = styled(Box)(({ is_blocked = 'false' }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 17,
    opacity: is_blocked === 'true' ? 0.5 : 1,
}));

const StepTitleBlock = styled(Typography)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 12,
}));

const StepTitle = styled(Typography)(({ theme }) => ({
    lineHeight: 1.2,
}));

const Coin = styled(Box)(({ theme }) => ({
    minWidth: 28,
    width: 28,
    height: 28,

    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    color: 'white',
    fontSize: 14,
    lineHeight: 1,

    backgroundColor: '#3B7AF6',
    borderRadius: 16,
}));

const Caption = styled(Typography)(({ theme }) => ({
    fontSize: 14,
    opacity: 0.8,
    lineHeight: 1.5,
    padding: '0 10px',
}));

const TeamControlPage = () => {
    const selectedLanguage = useRecoilValue(selectedLanguageAtom);
    const { teamControlPage, sessionCreateModal: sessionCreateModalData } = textData[selectedLanguage];
    const navigate = useNavigate();
    const { teamId } = useParams();
    const [sessionCreateModal, setSessionCreateModal] = useState(false);
    const [instructionsModal, setInstructionsModal] = useState(false);
    const [sharingUrl, setSharingUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [breadcrumbsData, setBreadcrumbsData] = useState({});
    const [userData, setUserData] = useRecoilState(userAtom);
    const [currentUserNumber, setCurrentUserNumber] = useState(userData.currentUserNumber);
    const [allUserNumber, setAllUserNumber] = useState(userData.allUserNumber);
    const setLanguage = useSetRecoilState(selectedLanguageAtom);

    const isSessionCreated = userData.isSessionCreated;
    const shouldShowAllButtons = !userData.team.is_registered_with_telegram || isSessionCreated;

    const updateSteps = useCallback(
        (steps) => {
            setUserData((prev) => ({
                ...prev,
                avaliableSteps: steps,
            }));
        },
        [setUserData],
    );

    const updateUserData = useCallback(() => {
        getUserData(userData?.userId, teamId)
            .then((user) => {
                setUserData(user);
                setLanguage(user.language);

                updateSteps(user.team.avaliable_steps);
                setCurrentUserNumber(user.team.current_users_number);
                setAllUserNumber(user.team.number_of_users);

                localStorage.setItem('user-id', user.userId);
                localStorage.setItem('username', user.username);
                localStorage.setItem('language', user.language);

                if (user?.isAccessFinished) {
                    navigate('/trial-end');
                }
            })
            .catch((error) => {
                console.error('Ошибка получения пользователя', error);
            });
    }, [setUserData, updateSteps, teamId, navigate]);

    const handleExcelButtonClick = (e) => {
        setLoading(true);
        e.preventDefault();
        getExcel(teamId)
            .then((res) => {
                // create file link in browser's memory
                const href = URL.createObjectURL(res.data);
                const fileName = res.headers['content-disposition'].split('=')[1].replaceAll('"', '');

                // create "a" HTML element with href to file & click
                const link = document.createElement('a');
                link.href = href;
                link.setAttribute('download', fileName); //or any other extension
                document.body.appendChild(link);
                link.click();

                // clean up "a" element & remove ObjectURL
                document.body.removeChild(link);
                URL.revokeObjectURL(href);
                setLoading(false);
            })
            .catch((err) => {
                console.error('Ошибка получения excel файла', err);
            });
    };

    const breadcrumbs = [
        {
            label: breadcrumbsData?.team?.name,
        },
        {
            label: sessionCreateModalData.breadcrumbs.current,
        },
    ];

    const handleSetThirdStep = () => {
        if (!userData?.avaliableSteps?.third) {
            updateStep('third')
                .then((res) => {
                    updateSteps(res.data?.team.avaliable_steps);
                })
                .catch((err) => {
                    console.error('Ошибка получения excel файла', err);
                });
        }
        navigate(`/admin/team/${teamId}/survey-results`);
    };

    function handleCreateSession(response) {
        if (response) {
            updateUserData();
        }
        setSessionCreateModal(false);
    }

    useEffect(() => {
        updateUserData();
    }, [updateUserData]);

    useEffect(() => {
        getTeamInfo(teamId)
            .then((r) => {
                setBreadcrumbsData(r.data);

                setSharingUrl(r.data?.team?.sharing_url);
            })
            .catch((err) => {
                console.error('Ошибка получения информации о команде', err);
            });
    }, []);

    useEffect(() => {
        if (userData.areValuesApproved) {
            return;
        }

        updateUserData();

        const interval = setInterval(() => {
            updateUserData();
        }, 5000);

        return () => clearInterval(interval);
    }, [userData.areValuesApproved, updateUserData]);

    if (window.Telegram?.WebApp?.initDataUnsafe?.user && !userData?.areValuesApproved) {
        return (
            <Layout link={'/admin'}>
                <StyledBreadcrumbs separator={<DotDivider style={{ width: 2, height: 2 }} />}>
                    <Link className="light" onClick={() => navigate('/admin')}>
                        {breadcrumbsData?.company?.name}
                    </Link>
                    <Link>{breadcrumbsData?.team?.name}</Link>
                    <Link className="current">{teamControlPage.breadcrumbs.current}</Link>
                </StyledBreadcrumbs>
                <ButtonsWrap gap={40}>
                    <PatternedButton
                        label={teamControlPage.buttons[0].title}
                        onClick={() => setInstructionsModal(true)}
                    />
                    <StepWrapper>
                        <StepTitleBlock>
                            <Coin>1</Coin>
                            <StepTitle>{teamControlPage.titles[0]}</StepTitle>
                        </StepTitleBlock>
                        <CopyButton
                            setInstructionsModal={setInstructionsModal}
                            sharingUrl={sharingUrl}
                            updateSteps={updateSteps}
                            isFirstStep={!userData?.avaliableSteps?.second}
                        />
                        <Caption>
                            {teamControlPage.titles[1]}
                            {currentUserNumber || '0'} {teamControlPage.outOf} {allUserNumber || '0'}
                        </Caption>
                    </StepWrapper>
                    <StepWrapper is_blocked={(!userData?.avaliableSteps?.second).toString()}>
                        <StepTitleBlock>
                            <Coin>2</Coin>
                            <StepTitle>{teamControlPage.titles[2]}</StepTitle>
                        </StepTitleBlock>
                        <PatternedButton
                            isGrayWhenDisabled={true}
                            label={teamControlPage.buttons[1].title}
                            disabled={!userData?.avaliableSteps?.second}
                            onClick={handleSetThirdStep}
                        />
                    </StepWrapper>
                    <StepWrapper is_blocked={(!userData?.avaliableSteps?.third).toString()}>
                        <StepTitleBlock>
                            <Coin>3</Coin>
                            <StepTitle>{teamControlPage.titles[3]}</StepTitle>
                        </StepTitleBlock>
                        <Caption>{teamControlPage.titles[4]}</Caption>
                        <Caption>{teamControlPage.titles[5]}</Caption>
                        <PatternedButton
                            isGrayWhenDisabled={true}
                            label={teamControlPage.buttons[2].title}
                            disabled={!userData?.avaliableSteps?.third}
                            onClick={() => navigate(`/admin/team/${teamId}/behaviors`)}
                        />
                    </StepWrapper>
                </ButtonsWrap>
                <InstructionsModal open={instructionsModal} onClose={() => setInstructionsModal(false)} />
            </Layout>
        );
    }

    return (
        <Layout link={'/admin'}>
            <StyledBreadcrumbs separator={<DotDivider style={{ width: 2, height: 2 }} />}>
                <Link className="light" onClick={() => navigate('/admin')}>
                    {breadcrumbsData?.company?.name}
                </Link>
                <Link>{breadcrumbsData?.team?.name}</Link>
                <Link className="current">{teamControlPage.breadcrumbs.current}</Link>
            </StyledBreadcrumbs>
            <ButtonsWrap>
                {shouldShowAllButtons && userData.isTeamMember && (
                    <PatternedButton
                        isAccent={true}
                        label={teamControlPage.buttons[3].title}
                        onClick={() => navigate(`/main`)}
                    />
                )}
                {shouldShowAllButtons && (
                    <PatternedButton
                        label={teamControlPage.buttons[11].title}
                        onClick={() => navigate(`/admin/team/${teamId}/users`)}
                    />
                )}
                {window.Telegram?.WebApp?.initDataUnsafe?.user && (
                    <>
                        <CopyButton setInstructionsModal={setInstructionsModal} sharingUrl={sharingUrl} />
                    </>
                )}
                <PatternedButton
                    label={teamControlPage.buttons[4].title}
                    onClick={() => setSessionCreateModal(true)}
                    isAccent={!isSessionCreated}
                    isShine={!isSessionCreated}
                />
                <PatternedButton
                    label={teamControlPage.buttons[5].title}
                    onClick={() => navigate(`/admin/team/${teamId}/session-history`)}
                />
                <PatternedButton
                    label={teamControlPage.buttons[6].title}
                    onClick={() => navigate(`/admin/team/${teamId}/behaviors`)}
                />

                {window.Telegram?.WebApp?.initDataUnsafe?.user && (
                    <PatternedButton
                        label={teamControlPage.buttons[7].title}
                        onClick={() => navigate(`/admin/team/${teamId}/survey-results`)}
                    />
                )}

                {shouldShowAllButtons && (
                    <>
                        <PatternedButton
                            label={teamControlPage.buttons[8].title}
                            onClick={() => navigate(`/admin/team/${teamId}/max-score`)}
                        />
                        <PatternedButton
                            label={loading ? teamControlPage.buttons[10].title : teamControlPage.buttons[9].title}
                            onClick={handleExcelButtonClick}
                            disabled={loading}
                        />
                    </>
                )}
            </ButtonsWrap>
            <SessionCreateModal
                open={sessionCreateModal}
                teamId={teamId}
                breadcrumbsData={breadcrumbs}
                onClose={() => setSessionCreateModal(false)}
                onCreate={handleCreateSession}
            />
        </Layout>
    );
};

export default TeamControlPage;

const CopyButton = ({ setInstructionsModal, sharingUrl, isFirstStep, updateSteps }) => {
    const selectedLanguage = useRecoilValue(selectedLanguageAtom);
    const { teamControlPage } = textData[selectedLanguage];
    const [isOpen, setIsOpen] = useState(false);

    const handleClick = () => {
        copyToClipboard();

        setIsOpen(true);

        if (isFirstStep) {
            updateStep('second')
                .then((res) => {
                    updateSteps(res.data?.team.avaliable_steps);
                })
                .catch((err) => {
                    console.error('Ошибка получения excel файла', err);
                });
        }
    };

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setIsOpen(false);
    };

    //Копирование в буфер обмена
    const copyToClipboard = async () => {
        if (!navigator.clipboard) {
            console.error('Clipboard API не поддерживается в этом браузере');
            return;
        }

        try {
            await navigator.clipboard.writeText(teamControlPage.clipboardText + sharingUrl);
        } catch (err) {
            console.error('Ошибка копирования: ', err);
        }
    };

    return (
        <>
            <PatternedButton
                disabled={sharingUrl?.trim().length === 0}
                endIcon={null}
                label={teamControlPage.copy}
                onClick={handleClick}
            />
            <Snackbar open={isOpen} autoHideDuration={2000} onClose={handleClose}>
                <Alert severity="success" variant="filled" sx={{ width: '100%', background: '#12CDA7' }}>
                    {teamControlPage.copied}
                </Alert>
            </Snackbar>
        </>
    );
};
